import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Check, Copy, Download, KeyRound, MoreHorizontal, Plus, Upload } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ImportUsersResult, UserAdmin } from '@tt/shared';
import { USER_ROLES } from '@tt/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Alert, Badge } from '@/components/ui/misc';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { NativeSelect } from '@/components/ui/native-select';
import {
  downloadUserData,
  useAdminGroups,
  useAdminUsers,
  useCreateUser,
  useGroupMutations,
  useImportUsers,
  useResetUserPassword,
  useUpdateUser,
  useUserAction,
} from '@/lib/admin-queries';
import { describeError, fieldErrorsOf } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { useMe } from '@/lib/queries';
import { useFormat } from '@/lib/use-format';
import { AdminPage, ConfirmDialog, DataTable, MultiSelect, Pagination } from './admin-ui';
import { resetPasswordBlocker } from './row-action-reasons';

type Pending = { user: UserAdmin; kind: 'delete' | 'anonymize' | 'reset-password' } | null;

/** Users: the list, the row actions, bulk group membership and the CSV import. */
export function AdminUsersPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const navigate = useNavigate();
  const search = useSearch({ from: '/app/admin/users' });
  const { data: me } = useMe();

  const [editing, setEditing] = useState<UserAdmin | 'new' | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  // The temporary password, held only while its dialog is open. It is never cached or refetched:
  // the server hands it over once and keeps no readable copy.
  const [temporary, setTemporary] = useState<{ user: UserAdmin; password: string } | null>(null);

  const page = search.page ?? 1;
  const users = useAdminUsers({
    q: search.q || undefined,
    role: search.role,
    status: search.status,
    groupId: search.groupId,
    page,
    pageSize: 25,
  });
  const groups = useAdminGroups({ pageSize: 200 });
  const action = useUserAction();
  const resetPassword = useResetUserPassword();
  const groupMutations = useGroupMutations();

  const setSearch = (next: Record<string, unknown>) =>
    void navigate({ to: '/admin/users', search: { ...search, page: 1, ...next }, replace: true });

  const run = (promise: Promise<unknown>, message: string) =>
    promise.then(
      () => toast.success(message),
      (error: unknown) => toast.error(describeError(error, t)),
    );

  const rows = users.data?.data ?? [];
  const groupOptions = (groups.data?.data ?? []).map((g) => ({ id: g.id, label: g.name }));
  const nameOf = (u: UserAdmin) => `${u.firstName} ${u.lastName}`;
  const allSelected = rows.length > 0 && rows.every((u) => selected.includes(u.id));

  return (
    <AdminPage
      title={t('admin.users.title')}
      description={t('admin.users.subtitle')}
      actions={
        <>
          <Button variant="outline" onClick={() => setImporting(true)}>
            <Upload className="h-4 w-4" aria-hidden="true" /> {t('admin.users.import')}
          </Button>
          <Button onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" aria-hidden="true" /> {t('admin.users.new')}
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-end gap-2">
        <Input
          type="search"
          defaultValue={search.q ?? ''}
          onChange={(e) => setSearch({ q: e.target.value || undefined })}
          placeholder={t('admin.users.search')}
          aria-label={t('admin.users.search')}
          className="max-w-xs"
        />
        <NativeSelect
          aria-label={t('admin.users.role')}
          value={search.role ?? ''}
          onChange={(e) => setSearch({ role: e.target.value || undefined })}
          className="w-36"
        >
          <option value="">{t('admin.all')}</option>
          {USER_ROLES.map((role) => (
            <option key={role} value={role}>
              {t(`roles.${role}`)}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label={t('admin.users.status')}
          value={search.status ?? ''}
          onChange={(e) => setSearch({ status: e.target.value || undefined })}
          className="w-36"
        >
          <option value="">{t('admin.all')}</option>
          {(['ACTIVE', 'INVITED', 'INACTIVE'] as const).map((status) => (
            <option key={status} value={status}>
              {t(`userStatus.${status}`)}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label={t('admin.users.groups')}
          value={search.groupId ?? ''}
          onChange={(e) => setSearch({ groupId: e.target.value || undefined })}
          className="w-44"
        >
          <option value="">{t('admin.all')}</option>
          {groupOptions.map((g) => (
            <option key={g.id} value={g.id}>
              {g.label}
            </option>
          ))}
        </NativeSelect>
      </div>

      {selected.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border bg-accent/40 px-3 py-2">
          <span className="text-sm" aria-live="polite">
            {t('admin.users.selected', { count: selected.length })}
          </span>
          <NativeSelect
            aria-label={t('admin.users.bulkAddToGroup')}
            value=""
            className="w-56"
            onChange={(e) => {
              const id = e.target.value;
              if (!id) return;
              void run(
                groupMutations.addMembers.mutateAsync({ id, ids: selected }),
                t('admin.groups.membersAdded', { count: selected.length }),
              ).then(() => setSelected([]));
            }}
          >
            <option value="">{t('admin.users.bulkAddToGroup')}</option>
            {groupOptions.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </NativeSelect>
          <Button variant="ghost" size="sm" onClick={() => setSelected([])}>
            {t('app.cancel')}
          </Button>
        </div>
      ) : null}

      <DataTable
        caption={t('admin.users.title')}
        isPending={users.isPending}
        error={users.isError ? describeError(users.error, t) : undefined}
        empty={t('admin.users.none')}
        rows={rows}
        rowKey={(u) => u.id}
        footer={
          <Pagination
            page={page}
            totalPages={users.data?.meta.totalPages ?? 1}
            total={users.data?.meta.total ?? 0}
            onChange={(next) =>
              void navigate({ to: '/admin/users', search: { ...search, page: next } })
            }
          />
        }
        columns={[
          {
            key: 'name',
            header: (
              <label className="inline-flex items-center gap-2 font-medium normal-case">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={allSelected}
                  aria-label={t('admin.users.selectAll')}
                  onChange={(e) => setSelected(e.target.checked ? rows.map((u) => u.id) : [])}
                />
                {t('admin.users.title')}
              </label>
            ),
            cell: (u) => (
              <label className="inline-flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={selected.includes(u.id)}
                  aria-label={t('admin.users.selectRow', { name: nameOf(u) })}
                  onChange={(e) =>
                    setSelected((prev) =>
                      e.target.checked ? [...prev, u.id] : prev.filter((id) => id !== u.id),
                    )
                  }
                />
                <span>
                  <span className="block font-medium">{nameOf(u)}</span>
                  <span className="block text-xs text-muted-foreground">{u.email}</span>
                </span>
              </label>
            ),
          },
          {
            key: 'role',
            header: t('admin.users.role'),
            cell: (u) => t(`roles.${u.role}`),
          },
          {
            key: 'status',
            header: t('admin.users.status'),
            cell: (u) => (
              <span className="inline-flex flex-wrap items-center gap-1">
                <Badge>{t(`userStatus.${u.status}`)}</Badge>
                {u.mustChangePassword ? (
                  <Badge title={t('admin.users.mustChangePasswordHint', { name: nameOf(u) })}>
                    {t('admin.users.mustChangePassword')}
                  </Badge>
                ) : null}
              </span>
            ),
          },
          {
            key: 'groups',
            header: t('admin.users.groups'),
            cell: (u) =>
              u.groups.length ? (
                u.groups.map((g) => g.name).join(', ')
              ) : (
                <span className="text-muted-foreground">{t('admin.users.noGroups')}</span>
              ),
          },
          {
            key: 'lastLogin',
            header: t('admin.users.lastLogin'),
            cell: (u) =>
              u.lastLoginAt ? (
                formatDate(u.lastLoginAt.slice(0, 10), fmt)
              ) : (
                <span className="text-muted-foreground">{t('admin.users.never')}</span>
              ),
          },
          {
            key: 'actions',
            header: <span className="sr-only">{t('admin.actions')}</span>,
            numeric: true,
            cell: (u) => (
              <RowActions
                user={u}
                isSelf={u.id === me?.id}
                onEdit={() => setEditing(u)}
                onAction={(kind) => {
                  if (kind === 'delete' || kind === 'anonymize' || kind === 'reset-password') {
                    setPending({ user: u, kind });
                    return;
                  }
                  if (kind === 'export') {
                    downloadUserData(u.id, nameOf(u));
                    return;
                  }
                  const messages = {
                    activate: t('admin.users.activated', { name: nameOf(u) }),
                    deactivate: t('admin.users.deactivated', { name: nameOf(u) }),
                    'resend-invite': t('admin.users.inviteSent'),
                  } as const;
                  void run(action.mutateAsync({ id: u.id, action: kind }), messages[kind]);
                }}
              />
            ),
          },
        ]}
      />

      {editing ? (
        <UserDialog
          user={editing === 'new' ? null : editing}
          groupOptions={groupOptions}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {importing ? <ImportDialog onClose={() => setImporting(false)} /> : null}

      <ConfirmDialog
        open={pending?.kind === 'delete'}
        onOpenChange={(open) => !open && setPending(null)}
        title={t('admin.users.deleteTitle', { name: pending ? nameOf(pending.user) : '' })}
        description={t('admin.users.deleteBody')}
        confirmLabel={t('app.delete')}
        onConfirm={() => {
          if (!pending) return;
          void run(
            action.mutateAsync({ id: pending.user.id, action: 'delete' }),
            t('admin.users.deleted', { name: nameOf(pending.user) }),
          );
        }}
      />
      <ConfirmDialog
        open={pending?.kind === 'reset-password'}
        onOpenChange={(open) => !open && setPending(null)}
        title={t('admin.users.resetPasswordTitle', { name: pending ? nameOf(pending.user) : '' })}
        description={t('admin.users.resetPasswordBody')}
        confirmLabel={t('admin.users.resetPassword')}
        onConfirm={() => {
          if (!pending) return;
          const user = pending.user;
          void resetPassword.mutateAsync(user.id).then(
            (result) => setTemporary({ user, password: result.temporaryPassword }),
            (error: unknown) => toast.error(describeError(error, t)),
          );
        }}
      />

      {temporary ? (
        <TemporaryPasswordDialog
          name={nameOf(temporary.user)}
          password={temporary.password}
          onClose={() => setTemporary(null)}
        />
      ) : null}

      <ConfirmDialog
        open={pending?.kind === 'anonymize'}
        onOpenChange={(open) => !open && setPending(null)}
        title={t('admin.users.anonymizeTitle', { name: pending ? nameOf(pending.user) : '' })}
        description={t('admin.users.anonymizeBody')}
        confirmLabel={t('admin.users.anonymize')}
        confirmWord="ANONYMIZE"
        onConfirm={() => {
          if (!pending) return;
          void run(
            action.mutateAsync({ id: pending.user.id, action: 'anonymize' }),
            t('admin.users.anonymized', { name: nameOf(pending.user) }),
          );
        }}
      />
    </AdminPage>
  );
}

/**
 * Shows the temporary password once. There is no "show again": the server returned it in that one
 * response and stores only its hash, so closing this dialog really is the end of it.
 */
function TemporaryPasswordDialog({
  name,
  password,
  onClose,
}: {
  name: string;
  password: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    // Clipboard access can be refused (an insecure origin, a permission prompt); the password is
    // selectable on screen either way, so a failure is not worth an error.
    void navigator.clipboard
      ?.writeText(password)
      .then(() => {
        setCopied(true);
        toast.success(t('admin.users.resetPasswordCopied'));
      })
      .catch(() => undefined);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.users.resetPasswordDoneTitle', { name })}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t('admin.users.resetPasswordDoneBody', { name })}
        </p>
        <div className="flex items-center gap-2">
          <code
            className="flex-1 select-all rounded-md border bg-muted px-3 py-2 font-mono text-base tracking-wide"
            data-testid="temporary-password"
          >
            {password}
          </code>
          <Button type="button" variant="outline" onClick={copy}>
            {copied ? (
              <Check className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4" aria-hidden="true" />
            )}
            {t('admin.users.resetPasswordCopy')}
          </Button>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {t('app.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type RowAction =
  | 'activate'
  | 'deactivate'
  | 'resend-invite'
  | 'reset-password'
  | 'delete'
  | 'anonymize'
  | 'export';

const ITEM_CLASS =
  'block w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent' +
  ' aria-disabled:cursor-not-allowed aria-disabled:opacity-60 aria-disabled:hover:bg-transparent';

/**
 * One action in the row menu. A `reason` makes it unavailable, and is shown.
 *
 * `aria-disabled` rather than `disabled`, because the reason is the whole point: a `disabled`
 * button leaves the tab order, so a keyboard or screen-reader user never reaches the explanation,
 * and `title` is not a reliable way to give it either — browsers disagree about whether a
 * disabled control has a tooltip at all. An administrator looking at a greyed-out "Reset
 * password" with no stated reason has no way to tell a rule from a bug.
 */
function Action({
  label,
  reason,
  onClick,
}: {
  label: ReactNode;
  reason?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={ITEM_CLASS}
      aria-disabled={reason ? true : undefined}
      onClick={reason ? undefined : onClick}
    >
      {label}
      {reason ? (
        <span className="mt-0.5 block text-xs font-normal text-muted-foreground">{reason}</span>
      ) : null}
    </button>
  );
}

/** Row actions in a menu, with the ones that cannot apply unavailable and explained. */
function RowActions({
  user,
  isSelf,
  onEdit,
  onAction,
}: {
  user: UserAdmin;
  isSelf: boolean;
  onEdit: () => void;
  onAction: (action: RowAction) => void;
}) {
  const { t } = useTranslation();
  const name = `${user.firstName} ${user.lastName}`;

  const notYourself = isSelf ? t('admin.users.notYourself') : undefined;
  const resetBlocker = resetPasswordBlocker(user, isSelf);

  return (
    <Popover>
      <PopoverTrigger
        aria-label={t('admin.actions') + ': ' + name}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1">
        <Action label={t('admin.users.editName', { name })} onClick={onEdit} />
        <Link to="/admin/timesheets" search={{ userId: user.id }} className={ITEM_CLASS}>
          {t('admin.users.timesheet')}
        </Link>
        {user.status === 'INVITED' ? (
          <Action label={t('admin.users.resendInvite')} onClick={() => onAction('resend-invite')} />
        ) : null}
        <Action
          label={
            user.status === 'INACTIVE' ? t('admin.users.activate') : t('admin.users.deactivate')
          }
          reason={notYourself}
          onClick={() => onAction(user.status === 'INACTIVE' ? 'activate' : 'deactivate')}
        />
        <Action
          label={
            <span className="inline-flex items-center gap-1.5">
              <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
              {t('admin.users.resetPassword')}
            </span>
          }
          reason={resetBlocker ? t(resetBlocker) : undefined}
          onClick={() => onAction('reset-password')}
        />
        <Action
          label={
            <span className="inline-flex items-center gap-1.5">
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              {t('admin.users.exportData')}
            </span>
          }
          onClick={() => onAction('export')}
        />
        <Action
          label={t('admin.users.anonymize')}
          reason={notYourself}
          onClick={() => onAction('anonymize')}
        />
        <Action label={t('app.delete')} reason={notYourself} onClick={() => onAction('delete')} />
      </PopoverContent>
    </Popover>
  );
}

function UserDialog({
  user,
  groupOptions,
  onClose,
}: {
  user: UserAdmin | null;
  groupOptions: { id: string; label: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const create = useCreateUser();
  const update = useUpdateUser();
  const [form, setForm] = useState({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    email: user?.email ?? '',
    role: user?.role ?? 'USER',
    timeZone: user?.timeZone ?? me?.organization.defaultTimeZone ?? 'UTC',
    groupIds: user?.groups.map((g) => g.id) ?? [],
  });
  const [error, setError] = useState<unknown>(null);
  const fieldErrors = fieldErrorsOf(error);

  const submit = async () => {
    setError(null);
    try {
      if (user) {
        await update.mutateAsync({ id: user.id, ...form });
        toast.success(t('admin.users.saved', { name: `${form.firstName} ${form.lastName}` }));
      } else {
        await create.mutateAsync(form);
        toast.success(t('admin.users.created', { name: `${form.firstName} ${form.lastName}` }));
      }
      onClose();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {user
              ? t('admin.users.editName', { name: `${user.firstName} ${user.lastName}` })
              : t('admin.users.new')}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('admin.users.firstName')} required error={fieldErrors.firstName}>
              {(props) => (
                <Input
                  {...props}
                  value={form.firstName}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  required
                />
              )}
            </Field>
            <Field label={t('admin.users.lastName')} required error={fieldErrors.lastName}>
              {(props) => (
                <Input
                  {...props}
                  value={form.lastName}
                  onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                  required
                />
              )}
            </Field>
          </div>
          <Field label={t('admin.users.email')} required error={fieldErrors.email}>
            {(props) => (
              <Input
                {...props}
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
            )}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('admin.users.role')} error={fieldErrors.role}>
              {(props) => (
                <NativeSelect
                  {...props}
                  value={form.role}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, role: e.target.value as 'USER' | 'ADMIN' }))
                  }
                >
                  {USER_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {t(`roles.${role}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('admin.users.timeZone')} error={fieldErrors.timeZone}>
              {(props) => (
                <Input
                  {...props}
                  value={form.timeZone}
                  onChange={(e) => setForm((f) => ({ ...f, timeZone: e.target.value }))}
                />
              )}
            </Field>
          </div>
          <Field label={t('admin.users.groups')}>
            {() => (
              <MultiSelect
                label={t('admin.users.groups')}
                options={groupOptions}
                selected={form.groupIds}
                emptyLabel={t('admin.users.noGroups')}
                onChange={(groupIds) => setForm((f) => ({ ...f, groupIds }))}
              />
            )}
          </Field>
          {error && Object.keys(fieldErrors).length === 0 ? (
            <p className="text-sm text-destructive" role="alert">
              {describeError(error, t)}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('app.cancel')}
            </Button>
            <Button type="submit" disabled={!form.email.trim() || !form.firstName.trim()}>
              {t('app.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Paste a CSV, check it, then run it. The check is the same code path as the import. */
function ImportDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const importUsers = useImportUsers();
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<ImportUsersResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  const go = async (dryRun: boolean) => {
    setError(null);
    try {
      const res = await importUsers.mutateAsync({ csv, dryRun });
      setResult(res);
      if (!dryRun) {
        toast.success(t('admin.users.importDone', { count: res.created }));
        onClose();
      }
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('admin.users.importTitle')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t('admin.users.importHelp')}</p>
        <Field label={t('admin.users.importPaste')} required>
          {(props) => (
            <textarea
              {...props}
              value={csv}
              onChange={(e) => {
                setCsv(e.target.value);
                setResult(null);
              }}
              rows={8}
              spellCheck={false}
              className="w-full rounded-md border bg-background px-3 py-2 font-mono text-xs"
              placeholder="email,firstName,lastName,role,timeZone,groups"
            />
          )}
        </Field>
        {result ? (
          <Alert variant={result.errors.length ? 'warning' : 'info'}>
            <p>
              {t('admin.users.importResult', {
                created: result.created,
                skipped: result.skipped,
                errors: result.errors.length,
              })}
            </p>
            {result.errors.length ? (
              <ul className="mt-2 list-disc pl-5 text-xs">
                {result.errors.map((e) => (
                  <li key={e.row}>
                    {t('admin.users.importRow', { row: e.row })}: {e.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </Alert>
        ) : null}
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {describeError(error, t)}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('app.cancel')}
          </Button>
          <Button variant="outline" disabled={!csv.trim()} onClick={() => void go(true)}>
            {t('admin.users.importPreview')}
          </Button>
          <Button
            disabled={!csv.trim() || !result || result.created === 0}
            onClick={() => void go(false)}
          >
            {t('admin.users.importRun')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
